/**
 * RoutingActionBuilder - Construtor de sequência de ações para regras de roteamento
 * 
 * Similar ao CampaignMessageBuilder, permite adicionar múltiplas ações:
 * - Dados: departamento, agente, tag, prioridade, fase
 * - Envio: texto, imagem, vídeo, áudio, documento, link
 * - Controle: delay
 * 
 * Funcionalidades:
 * - Drag & drop para reordenar
 * - Preview visual de cada ação
 * - Integração com Media Gallery
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useTenantMembers } from '@/hooks/useTenantMembers';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  MessageSquare,
  Image,
  Video,
  Music,
  FileText,
  Timer,
  Link,
  Building2,
  User,
  Tag,
  MinusCircle,  // v2.1: Ícone para remover tag (mesmo do Flow)
  Star,
  LayoutDashboard,
  Users,  // v2.2: Ícone para fila global
  MousePointerClick,  // v2.3: Ícone para botão com link
  FileSignature,  // v2.4: Ícone para template WhatsApp
  Plus,
  Database,
  Send,
} from 'lucide-react';
import { RoutingActionItem, actionTypeConfig, dataActionTypes, sendActionTypes } from './types';
import { RoutingActionCard } from './RoutingActionCard';
import { AddDelayActionDialog } from './dialogs/AddDelayActionDialog';
import { AddTextActionDialog } from './dialogs/AddTextActionDialog';
import { AddMediaActionDialog } from './dialogs/AddMediaActionDialog';
import { AddLinkActionDialog } from './dialogs/AddLinkActionDialog';
import { AddButtonLinkActionDialog } from './dialogs/AddButtonLinkActionDialog';
import { AddTemplateActionDialog } from './dialogs/AddTemplateActionDialog';
import { AddDataActionDialog } from './dialogs/AddDataActionDialog';
import { RoutingActionType } from '@/pages/tenant/RoutingRules';
import { createLogger } from '@/lib/logger';

const logger = createLogger('RoutingActionBuilder');

// =====================================================
// INTERFACES
// =====================================================

interface RoutingActionBuilderProps {
  items: RoutingActionItem[];
  onItemsChange: (items: RoutingActionItem[]) => void;
  /** IDs das inboxes selecionadas na regra (para filtrar templates) */
  selectedInboxIds?: string[];
}

// Configuração dos botões de ação
const actionButtons = [
  // Envio
  { type: 'send_text' as RoutingActionType, icon: MessageSquare, group: 'send' },
  { type: 'send_image' as RoutingActionType, icon: Image, group: 'send' },
  { type: 'send_video' as RoutingActionType, icon: Video, group: 'send' },
  { type: 'send_audio' as RoutingActionType, icon: Music, group: 'send' },
  { type: 'send_document' as RoutingActionType, icon: FileText, group: 'send' },
  { type: 'send_link' as RoutingActionType, icon: Link, group: 'send' },
  { type: 'send_button_link' as RoutingActionType, icon: MousePointerClick, group: 'send' },  // v2.3
  { type: 'send_template' as RoutingActionType, icon: FileSignature, group: 'send' },  // v2.4
  // Controle
  { type: 'delay' as any, icon: Timer, group: 'control' },
  // Dados
  { type: 'assign_department' as RoutingActionType, icon: Building2, group: 'data' },
  { type: 'assign_agent' as RoutingActionType, icon: User, group: 'data' },
  { type: 'add_tag' as RoutingActionType, icon: Tag, group: 'data' },
  { type: 'remove_tag' as RoutingActionType, icon: MinusCircle, group: 'data' },  // v2.1
  { type: 'set_priority' as RoutingActionType, icon: Star, group: 'data' },
  { type: 'assign_phase' as RoutingActionType, icon: LayoutDashboard, group: 'data' },
  { type: 'assign_global_queue' as RoutingActionType, icon: Users, group: 'data' },  // v2.2
];

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

export function RoutingActionBuilder({
  items,
  onItemsChange,
  selectedInboxIds,
}: RoutingActionBuilderProps) {
  const { currentTenant } = useAuth();
  
  // v2.0.0: Membros do tenant via hook centralizado (cache compartilhado, RPC server-side)
  const { data: tenantMembers = [] } = useTenantMembers({ onlyActive: false });
  
  // Dados para exibição de nomes
  const [departmentNames, setDepartmentNames] = useState<Record<string, string>>({});
  const [tagNames, setTagNames] = useState<Record<string, string>>({});
  const [phaseNames, setPhaseNames] = useState<Record<string, string>>({});
  const [queueNames, setQueueNames] = useState<Record<string, string>>({});  // v2.2: Filas globais
  
  // Mapa derivado: agent_id (user_id) -> full_name/email
  const agentNames: Record<string, string> = {};
  tenantMembers.forEach(m => {
    agentNames[m.user_id] = m.full_name || m.email || '';
  });
  
  // Estado para dialogs
  const [delayDialogOpen, setDelayDialogOpen] = useState(false);
  const [textDialogOpen, setTextDialogOpen] = useState(false);
  const [mediaDialogOpen, setMediaDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [buttonLinkDialogOpen, setButtonLinkDialogOpen] = useState(false);  // v2.3
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);  // v2.4: Template WhatsApp
  const [dataDialogOpen, setDataDialogOpen] = useState(false);
  const [selectedActionType, setSelectedActionType] = useState<RoutingActionType>('assign_department');
  
  // Estado para edição
  const [editingItem, setEditingItem] = useState<RoutingActionItem | null>(null);

  // Estado para drag & drop
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  // Carrega nomes para exibição
  useEffect(() => {
    if (currentTenant?.id) {
      fetchNames();
    }
  }, [currentTenant?.id, items]);

  // Carrega nomes de departamentos, tags, fases e filas via RPCs server-side
  const fetchNames = async () => {
    if (!currentTenant?.id) return;

    // Departamentos via RPC
    const { data: depts } = await supabase.rpc('get_tenant_departments_full', {
      p_tenant_hint: currentTenant.id,
    });
    const deptMap: Record<string, string> = {};
    ((Array.isArray(depts) ? depts : []) as Array<any>).forEach(d => { deptMap[d.id] = d.name; });
    setDepartmentNames(deptMap);

    // Tags via RPC
    const { data: tags } = await supabase.rpc('get_tenant_contact_tags', {
      p_tenant_hint: currentTenant.id,
    });
    const tagMap: Record<string, string> = {};
    ((tags || []) as Array<any>).forEach(t => { tagMap[t.id] = t.name; });
    setTagNames(tagMap);

    // Fases via RPC consolidada (categorias + fases)
    const { data: catsData } = await supabase.rpc('get_tenant_kanban_categories_with_phases', {
      p_tenant_hint: currentTenant.id,
    });
    const phaseMap: Record<string, string> = {};
    ((Array.isArray(catsData) ? catsData : []) as Array<any>).forEach(c => {
      ((c.phases || []) as Array<any>).forEach(p => { phaseMap[p.id] = p.name; });
    });
    setPhaseNames(phaseMap);

    // v2.2: Filas de distribuição via RPC (filtro client-side por context_type/ativos)
    const { data: queues } = await supabase.rpc('get_distribution_queues_with_stats', {
      p_tenant_hint: currentTenant.id,
    });
    const queueMap: Record<string, string> = {};
    ((Array.isArray(queues) ? queues : []) as Array<any>)
      .filter(q => q.is_active && ['chat', 'all'].includes(q.context_type))
      .forEach(q => { queueMap[q.id] = q.name; });
    setQueueNames(queueMap);
  };

  // =====================================================
  // HANDLERS
  // =====================================================

  const handleAddAction = (type: RoutingActionType | 'delay') => {
    setEditingItem(null);
    setSelectedActionType(type as RoutingActionType);
    
    if (type === 'delay') {
      setDelayDialogOpen(true);
    } else if (type === 'send_text') {
      setTextDialogOpen(true);
    } else if (['send_image', 'send_video', 'send_audio', 'send_document'].includes(type)) {
      setMediaDialogOpen(true);
    } else if (type === 'send_link') {
      setLinkDialogOpen(true);
    } else if (type === 'send_button_link') {
      setButtonLinkDialogOpen(true);
    } else if (type === 'send_template') {
      setTemplateDialogOpen(true);
    } else if (dataActionTypes.includes(type as RoutingActionType)) {
      setDataDialogOpen(true);
    }
  };

  const handleEditItem = (item: RoutingActionItem) => {
    setEditingItem(item);
    setSelectedActionType(item.action_type);
    
    if (item.action_type === ('delay' as any)) {
      setDelayDialogOpen(true);
    } else if (item.action_type === 'send_text') {
      setTextDialogOpen(true);
    } else if (['send_image', 'send_video', 'send_audio', 'send_document'].includes(item.action_type)) {
      setMediaDialogOpen(true);
    } else if (item.action_type === 'send_link') {
      setLinkDialogOpen(true);
    } else if (item.action_type === 'send_button_link') {
      setButtonLinkDialogOpen(true);
    } else if (item.action_type === 'send_template') {
      setTemplateDialogOpen(true);
    } else if (dataActionTypes.includes(item.action_type)) {
      setDataDialogOpen(true);
    }
  };

  const handleRemoveItem = (itemId: string) => {
    const newItems = items.filter(item => item.id !== itemId)
      .map((item, index) => ({ ...item, sort_order: index }));
    onItemsChange(newItems);
  };

  // Salvar delay
  const handleSaveDelay = (seconds: number) => {
    const newItem: RoutingActionItem = {
      id: editingItem?.id || crypto.randomUUID(),
      action_type: 'delay' as any,
      action_value: {},
      sort_order: editingItem ? editingItem.sort_order : items.length,
      delay_seconds: seconds,
    };
    saveItem(newItem);
    setDelayDialogOpen(false);
  };

  // Salvar texto
  const handleSaveText = (message: string) => {
    const newItem: RoutingActionItem = {
      id: editingItem?.id || crypto.randomUUID(),
      action_type: 'send_text',
      action_value: { message },
      sort_order: editingItem ? editingItem.sort_order : items.length,
      delay_seconds: 0,
    };
    saveItem(newItem);
    setTextDialogOpen(false);
  };

  // Salvar mídia
  const handleSaveMedia = (data: any) => {
    const newItem: RoutingActionItem = {
      id: editingItem?.id || crypto.randomUUID(),
      action_type: selectedActionType,
      action_value: data,
      sort_order: editingItem ? editingItem.sort_order : items.length,
      delay_seconds: 0,
    };
    saveItem(newItem);
    setMediaDialogOpen(false);
  };

  // Salvar link
  const handleSaveLink = (data: any) => {
    const newItem: RoutingActionItem = {
      id: editingItem?.id || crypto.randomUUID(),
      action_type: 'send_link',
      action_value: data,
      sort_order: editingItem ? editingItem.sort_order : items.length,
      delay_seconds: 0,
    };
    saveItem(newItem);
    setLinkDialogOpen(false);
  };

  // v2.3: Salvar botão com link
  const handleSaveButtonLink = (data: any) => {
    const newItem: RoutingActionItem = {
      id: editingItem?.id || crypto.randomUUID(),
      action_type: 'send_button_link',
      action_value: data,
      sort_order: editingItem ? editingItem.sort_order : items.length,
      delay_seconds: 0,
    };
    saveItem(newItem);
    setButtonLinkDialogOpen(false);
  };

  // v2.4: Salvar template WhatsApp
  const handleSaveTemplate = (data: any) => {
    const newItem: RoutingActionItem = {
      id: editingItem?.id || crypto.randomUUID(),
      action_type: 'send_template',
      action_value: data,
      sort_order: editingItem ? editingItem.sort_order : items.length,
      delay_seconds: 0,
    };
    saveItem(newItem);
    setTemplateDialogOpen(false);
  };

  // Salvar ação de dados
  const handleSaveData = (data: any) => {
    const newItem: RoutingActionItem = {
      id: editingItem?.id || crypto.randomUUID(),
      action_type: selectedActionType,
      action_value: data,
      sort_order: editingItem ? editingItem.sort_order : items.length,
      delay_seconds: 0,
    };
    saveItem(newItem);
    setDataDialogOpen(false);
  };

  const saveItem = (newItem: RoutingActionItem) => {
    if (editingItem) {
      onItemsChange(items.map(item => item.id === editingItem.id ? newItem : item));
    } else {
      onItemsChange([...items, newItem]);
    }
    setEditingItem(null);
  };

  // Drag & drop handlers
  const handleDragStart = (index: number) => setDraggedIndex(index);
  
  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newItems = [...items];
    const draggedItem = newItems[draggedIndex];
    newItems.splice(draggedIndex, 1);
    newItems.splice(index, 0, draggedItem);

    const reorderedItems = newItems.map((item, idx) => ({ ...item, sort_order: idx }));
    onItemsChange(reorderedItems);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => setDraggedIndex(null);

  // =====================================================
  // RENDERIZAÇÃO
  // =====================================================

  return (
    <div className="space-y-4">
      {/* Botões para adicionar ações - Design profissional */}
      <div className="space-y-4">
        {/* Ações de Envio */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Send className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium text-muted-foreground">Enviar</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {actionButtons.filter(b => b.group === 'send').map(({ type, icon: Icon }) => {
              const config = actionTypeConfig[type as keyof typeof actionTypeConfig];
              return (
                <Button
                  key={type}
                  variant="outline"
                  size="sm"
                  onClick={() => handleAddAction(type)}
                  className="gap-2 h-8 px-3 font-medium transition-all hover:bg-muted/60 hover:border-primary/30 border-border/60"
                >
                  <Icon className="h-4 w-4" />
                  <span>{config?.label}</span>
                </Button>
              );
            })}
            {/* Delay */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleAddAction('delay' as any)}
              className="gap-2 h-8 px-3 font-medium transition-all hover:bg-muted/60 hover:border-primary/30 border-border/60"
            >
              <Timer className="h-4 w-4" />
              <span>Delay</span>
            </Button>
          </div>
        </div>

        {/* Ações de Dados */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Database className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium text-muted-foreground">Dados</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {actionButtons.filter(b => b.group === 'data').map(({ type, icon: Icon }) => {
              const config = actionTypeConfig[type as keyof typeof actionTypeConfig];
              return (
                <Button
                  key={type}
                  variant="outline"
                  size="sm"
                  onClick={() => handleAddAction(type)}
                  className="gap-2 h-8 px-3 font-medium transition-all hover:bg-muted/60 hover:border-primary/30 border-border/60"
                >
                  <Icon className="h-4 w-4" />
                  <span>{config?.label}</span>
                </Button>
              );
            })}
          </div>
        </div>
      </div>

      <Separator />

      {/* Lista de ações */}
      <ScrollArea className="h-[200px] border rounded-lg p-3">
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
            <Plus className="h-10 w-10 mb-3 opacity-50" />
            <p className="text-center text-sm">
              Adicione ações à sua sequência
            </p>
            <p className="text-xs text-center mt-1">
              Clique nos botões acima para começar
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {items
              .sort((a, b) => a.sort_order - b.sort_order)
              .map((item, index) => (
                <div
                  key={item.id}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`transition-opacity ${draggedIndex === index ? 'opacity-50' : 'opacity-100'}`}
                >
                  <RoutingActionCard
                    item={item}
                    index={index}
                    onEdit={() => handleEditItem(item)}
                    onRemove={() => handleRemoveItem(item.id)}
                    departmentName={departmentNames[(item.action_value as any)?.department_id]}
                    agentName={agentNames[(item.action_value as any)?.agent_id]}
                    tagName={tagNames[(item.action_value as any)?.tag_id]}
                    phaseName={phaseNames[(item.action_value as any)?.phase_id]}
                    queueName={queueNames[(item.action_value as any)?.queue_id]}
                  />
                </div>
              ))}
          </div>
        )}
      </ScrollArea>

      {/* Contador */}
      {items.length > 0 && (
        <div className="text-sm text-muted-foreground">
          {items.length} ação(ões) na sequência
        </div>
      )}

      {/* Dialogs */}
      <AddDelayActionDialog
        open={delayDialogOpen}
        onOpenChange={setDelayDialogOpen}
        onSave={handleSaveDelay}
        initialValue={editingItem?.delay_seconds}
      />

      <AddTextActionDialog
        open={textDialogOpen}
        onOpenChange={setTextDialogOpen}
        onSave={handleSaveText}
        initialValue={(editingItem?.action_value as any)?.message}
      />

      <AddMediaActionDialog
        open={mediaDialogOpen}
        onOpenChange={setMediaDialogOpen}
        onSave={handleSaveMedia}
        actionType={selectedActionType}
        initialValue={editingItem?.action_value}
      />

      <AddLinkActionDialog
        open={linkDialogOpen}
        onOpenChange={setLinkDialogOpen}
        onSave={handleSaveLink}
        initialValue={editingItem?.action_value}
      />

      {/* v2.3: Dialog de botão com link */}
      <AddButtonLinkActionDialog
        open={buttonLinkDialogOpen}
        onOpenChange={setButtonLinkDialogOpen}
        onSave={handleSaveButtonLink}
        initialValue={editingItem?.action_value}
      />

      {/* v2.4: Dialog de template WhatsApp */}
      <AddTemplateActionDialog
        open={templateDialogOpen}
        onOpenChange={setTemplateDialogOpen}
        onSave={handleSaveTemplate}
        initialValue={editingItem?.action_value}
        selectedInboxIds={selectedInboxIds}
      />

      <AddDataActionDialog
        open={dataDialogOpen}
        onOpenChange={setDataDialogOpen}
        onSave={handleSaveData}
        actionType={selectedActionType}
        initialValue={editingItem?.action_value}
      />
    </div>
  );
}
